import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import { adminAPI } from '../../services/api';

function SupportTeamManagement() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', phone: '' });
  const [actionLoading, setActionLoading] = useState(null);
  const [allRestaurants, setAllRestaurants] = useState([]);
  const [selectedRestaurants, setSelectedRestaurants] = useState([]);
  const [restaurantSearch, setRestaurantSearch] = useState('');

  useEffect(() => {
    fetchMembers();
    fetchRestaurants();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSupportTeamMembers();
      setMembers(response.data.data.members);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to load support team' });
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurants = async () => {
    try {
      const response = await adminAPI.getAllRestaurants({ limit: 500 });
      setAllRestaurants(response.data.data.restaurants || []);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
    }
  };

  const toggleRestaurant = (restaurantId) => {
    setSelectedRestaurants(prev =>
      prev.includes(restaurantId) ? prev.filter(id => id !== restaurantId) : [...prev, restaurantId]
    );
  };

  const filteredRestaurants = allRestaurants.filter(r =>
    r.name?.toLowerCase().includes(restaurantSearch.toLowerCase()) ||
    r.address?.city?.toLowerCase().includes(restaurantSearch.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || (!editingMember && !formData.password)) {
      setMessage({ type: 'error', text: 'Name, email, and password are required' });
      return;
    }

    try {
      setActionLoading('form');
      if (editingMember) {
        const updateData = { name: formData.name, email: formData.email, phone: formData.phone, assignedRestaurants: selectedRestaurants };
        if (formData.password) updateData.password = formData.password;
        await adminAPI.updateSupportTeamMember(editingMember._id, updateData);
        setMessage({ type: 'success', text: 'Member updated successfully' });
      } else {
        await adminAPI.createSupportTeamMember({ ...formData, assignedRestaurants: selectedRestaurants });
        setMessage({ type: 'success', text: 'Support team member created successfully' });
      }
      setShowForm(false);
      setEditingMember(null);
      setSelectedRestaurants([]);
      setFormData({ name: '', email: '', password: '', phone: '' });
      fetchMembers();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Operation failed' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleStatus = async (member) => {
    try {
      setActionLoading(member._id);
      await adminAPI.toggleSupportMemberStatus(member._id);
      setMessage({ type: 'success', text: `${member.name} ${member.isActive ? 'deactivated' : 'activated'}` });
      fetchMembers();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update status' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Are you sure you want to delete ${member.name}? This cannot be undone.`)) return;
    try {
      setActionLoading(member._id);
      await adminAPI.deleteSupportTeamMember(member._id);
      setMessage({ type: 'success', text: `${member.name} deleted` });
      fetchMembers();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to delete' });
    } finally {
      setActionLoading(null);
    }
  };

  const startEdit = (member) => {
    setEditingMember(member);
    setFormData({ name: member.name, email: member.email, password: '', phone: member.phone || '' });
    setSelectedRestaurants((member.assignedRestaurants || []).map(r => r._id || r));
    setShowForm(true);
  };

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>Support Team Management</h1>
            <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '14px' }}>
              Manage support team members who handle customer tickets at <strong>/support-team/login</strong>
            </p>
          </div>
          <button
            onClick={() => { setShowForm(!showForm); setEditingMember(null); setFormData({ name: '', email: '', password: '', phone: '' }); setSelectedRestaurants([]); setRestaurantSearch(''); }}
            style={{
              padding: '10px 20px', background: showForm ? '#64748b' : '#f43f5e', color: 'white',
              border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px'
            }}
          >
            {showForm ? 'Cancel' : '+ Add Member'}
          </button>
        </div>

        {message.text && (
          <div style={{
            padding: '12px 16px', marginBottom: '16px', borderRadius: '8px',
            background: message.type === 'error' ? '#fef2f2' : '#f0fdf4',
            color: message.type === 'error' ? '#dc2626' : '#16a34a',
            border: `1px solid ${message.type === 'error' ? '#fecaca' : '#bbf7d0'}`
          }}>
            {message.text}
          </div>
        )}

        {showForm && (
          <div style={{
            padding: '24px', background: '#fff', borderRadius: '12px', marginBottom: '24px',
            border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px' }}>
              {editingMember ? `Edit: ${editingMember.name}` : 'Add New Support Team Member'}
            </h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Full Name *</label>
                  <input
                    type="text" value={formData.name} required
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Email *</label>
                  <input
                    type="email" value={formData.email} required
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                    placeholder="email@foodwinc.com"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                    Password {editingMember ? '(leave blank to keep current)' : '*'}
                  </label>
                  <input
                    type="password" value={formData.password} required={!editingMember}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                    placeholder={editingMember ? 'Leave blank to keep' : 'Min 8 characters'}
                    minLength={formData.password ? 8 : undefined}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>Phone (optional)</label>
                  <input
                    type="text" value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>

              {/* Assigned Restaurants */}
              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                  Assigned Restaurants ({selectedRestaurants.length} selected)
                </label>
                {selectedRestaurants.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                    {selectedRestaurants.map(id => {
                      const r = allRestaurants.find(x => x._id === id);
                      return r ? (
                        <span key={id} style={{
                          padding: '4px 10px', background: '#dcfce7', color: '#16a34a', borderRadius: '20px',
                          fontSize: '12px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px'
                        }}>
                          {r.name}
                          <span onClick={() => toggleRestaurant(id)} style={{ cursor: 'pointer', fontWeight: '700' }}>&times;</span>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
                <input
                  type="text" value={restaurantSearch}
                  onChange={(e) => setRestaurantSearch(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', marginBottom: '6px' }}
                  placeholder="Search restaurants..."
                />
                <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  {filteredRestaurants.map(r => (
                    <div
                      key={r._id}
                      onClick={() => toggleRestaurant(r._id)}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        background: selectedRestaurants.includes(r._id) ? '#f0fdf4' : '#fff',
                        borderBottom: '1px solid #f1f5f9'
                      }}
                    >
                      <span>{r.name} <span style={{ color: '#94a3b8' }}>— {r.address?.city || ''}</span></span>
                      {selectedRestaurants.includes(r._id) && <span style={{ color: '#16a34a', fontWeight: '700' }}>&#10003;</span>}
                    </div>
                  ))}
                  {filteredRestaurants.length === 0 && (
                    <div style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No restaurants found</div>
                  )}
                </div>
              </div>

              <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
                <button
                  type="submit" disabled={actionLoading === 'form'}
                  style={{
                    padding: '10px 24px', background: '#f43f5e', color: 'white', border: 'none',
                    borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px',
                    opacity: actionLoading === 'form' ? 0.6 : 1
                  }}
                >
                  {actionLoading === 'form' ? 'Saving...' : editingMember ? 'Update Member' : 'Create Member'}
                </button>
                <button
                  type="button" onClick={() => { setShowForm(false); setEditingMember(null); }}
                  style={{
                    padding: '10px 24px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
                    borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#94a3b8' }}>Loading...</div>
        ) : members.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '48px', background: '#f8fafc', borderRadius: '12px',
            border: '1px solid #e2e8f0'
          }}>
            <p style={{ fontSize: '18px', fontWeight: '600', color: '#475569' }}>No support team members yet</p>
            <p style={{ color: '#94a3b8', margin: '8px 0 0' }}>Click "Add Member" to create the first support agent</p>
          </div>
        ) : (
          <div style={{
            background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Name</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Email</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Phone</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Restaurants</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Created</th>
                  <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#64748b' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px', fontWeight: '500' }}>{member.name}</td>
                    <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '14px' }}>{member.email}</td>
                    <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '14px' }}>{member.phone || '-'}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '13px' }}>
                      {(member.assignedRestaurants || []).length > 0 ? (
                        <span title={(member.assignedRestaurants || []).map(r => r.name || r).join(', ')} style={{
                          padding: '4px 10px', background: '#e0f2fe', color: '#0369a1', borderRadius: '20px', fontSize: '12px', fontWeight: '600', cursor: 'default'
                        }}>
                          {(member.assignedRestaurants || []).length} assigned
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>None</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600',
                        background: member.isActive ? '#dcfce7' : '#fef2f2',
                        color: member.isActive ? '#16a34a' : '#dc2626'
                      }}>
                        {member.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: '13px' }}>
                      {new Date(member.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => startEdit(member)}
                          style={{
                            padding: '6px 12px', background: '#f1f5f9', border: '1px solid #e2e8f0',
                            borderRadius: '6px', cursor: 'pointer', fontSize: '13px'
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleStatus(member)}
                          disabled={actionLoading === member._id}
                          style={{
                            padding: '6px 12px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                            background: member.isActive ? '#fef3c7' : '#dcfce7',
                            color: member.isActive ? '#92400e' : '#16a34a',
                            opacity: actionLoading === member._id ? 0.6 : 1
                          }}
                        >
                          {member.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDelete(member)}
                          disabled={actionLoading === member._id}
                          style={{
                            padding: '6px 12px', background: '#fef2f2', color: '#dc2626', border: 'none',
                            borderRadius: '6px', cursor: 'pointer', fontSize: '13px',
                            opacity: actionLoading === member._id ? 0.6 : 1
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default SupportTeamManagement;
